import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { Marker, InfoWindow, Polyline } from 'google-maps-react';
import dayjs from 'dayjs';
import { VscLoading } from 'react-icons/vsc';
import { MdClose } from 'react-icons/md';
import { FaClock, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { IoMdNavigate } from 'react-icons/io';
import { toastWrapper } from '../../../_helpers';
import { css } from 'twin.macro';

import {
    EventDetails,
    EventMarker,
    InfoWindowContent,
    LoadingOverlay,
    NextEventBadge,
    NoEventsMessage,
    OngoingEventBadge,
    RouteInfo,
    RouteItem,
    RoutesList,
    RoutesPanel,
    RoutesPanelHeader,
} from '../styles/EventRouteOverlayStyles';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import Avatar from '@/components/Admin/reusableComponents/Avatar';
import { eventTypeIcon } from '../GeoLocation';

const Component = ({ visible = false, google, currentPosition, events = [], onToggle = () => {}, mapRef, map }) => {
    const [routes, setRoutes] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showPanel, setShowPanel] = useState(true);
    const [loading, setLoading] = useState(false);
    const [activeMarker, setActiveMarker] = useState(null);
    const [infoWindowKey, setInfoWindowKey] = useState(Math.random());

    // Sort events by start time
    const sortedEvents = useMemo(() => {
        if (!events || !events.length) return [];

        return [...events].sort((a, b) => {
            const dateA = new Date(a.latestDatePointer || a.date);
            const dateB = new Date(b.latestDatePointer || b.date);
            return dateA - dateB;
        });
    }, [events]);

    const nextEvent = useMemo(() => {
        const now = new Date();
        const next = sortedEvents.find((event) => {
            const eventDate = new Date(event.latestDatePointer || event.date);
            return eventDate > now;
        });
        return next || sortedEvents[0];
    }, [sortedEvents]);

    const calculateRoutes = useCallback(async () => {
        if (!visible || !google || !currentPosition || !sortedEvents.length) return;

        try {
            setLoading(true);

            const directionsService = new google.maps.DirectionsService();
            const origin = new google.maps.LatLng(currentPosition.lat, currentPosition.lng);
            const routePromises = sortedEvents.map(async (event) => {
                if ((!event.location?.lat || !event.location?.lng) && event.location?.address) {
                    const geocoder = new google.maps.Geocoder();
                    const { results } = (await geocoder.geocode({ address: event.location.address })) || {};
                    if (results && results.length > 0) {
                        event.location.lat = results[0].geometry.location.lat();
                        event.location.lng = results[0].geometry.location.lng();
                    }
                }
                if (!event.location?.lat || !event.location?.lng) return null;

                const destination = new google.maps.LatLng(event.location.lat, event.location.lng);

                try {
                    const result = await new Promise((resolve, reject) => {
                        directionsService.route(
                            {
                                origin,
                                destination,
                                travelMode: google.maps.TravelMode.DRIVING,
                                unitSystem: google.maps.UnitSystem.IMPERIAL,
                            },
                            (response, status) => {
                                if (status === google.maps.DirectionsStatus.OK) {
                                    resolve(response);
                                } else {
                                    reject(new Error(`Directions request failed: ${status}`));
                                }
                            }
                        );
                    });

                    const route = result.routes[0];
                    const leg = route.legs[0];

                    let points = [];
                    try {
                        if (route.overview_polyline && route.overview_polyline.points) {
                            points = google.maps.geometry.encoding.decodePath(route.overview_polyline.points);
                        } else if (route.overview_polyline) {
                            points = google.maps.geometry.encoding.decodePath(route.overview_polyline);
                        }

                        if (points.length === 0 && leg.steps) {
                            points = leg.steps.flatMap((step) =>
                                google.maps.geometry.encoding.decodePath(step.polyline.points)
                            );
                        }
                    } catch (error) {
                        console.error('Error decoding polyline:', error);
                        // Fallback: Create simple line from origin to destination
                        points = [origin, destination];
                    }

                    return {
                        event,
                        points: points,
                        distance: leg.distance?.text || 'N/A',
                        distanceValue: leg.distance?.value || 0,
                        duration: leg.duration?.text || 'N/A',
                        durationValue: leg.duration?.value || 0,
                        startLocation: leg.start_location,
                        endLocation: leg.end_location,
                        steps: leg.steps,
                    };
                } catch (error) {
                    console.error(`Error calculating route to event ${event.title}:`, error);
                    return {
                        event,
                        error: true,
                    };
                }
            });

            const routes = await Promise.all(routePromises);
            setRoutes(routes.filter((route) => route !== null));
        } catch (error) {
            console.error('Error calculating routes:', error);
            toastWrapper.error('Failed to calculate routes between events');
        } finally {
            setLoading(false);
        }
    }, [visible, google, currentPosition, sortedEvents]);

    useEffect(() => {
        if (visible) {
            calculateRoutes();
        }
    }, [visible, calculateRoutes]);

    const handleMarkerClick = (event, marker) => {
        setSelectedEvent(event);
        setActiveMarker(marker);
        setInfoWindowKey(Math.random());
        centerOnEvent(event);
    };

    const handleInfoWindowClose = () => {
        setSelectedEvent(null);
        setActiveMarker(null);
    };

    const handleEventItemClick = (event) => {
        setSelectedEvent(event);
        setActiveMarker(null);
        setInfoWindowKey(Math.random());
        centerOnEvent(event);
    };

    const openNavigation = (lat, lng) => {
        if (!lat || !lng) return;

        const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        window.open(url, '_blank');
    };

    const centerOnEvent = (event) => {
        if (!event?.location?.lat || !event?.location?.lng || !mapRef?.current?.map) return;

        mapRef.current.map.setCenter({
            lat: event.location.lat,
            lng: event.location.lng,
        });

        mapRef.current.map.setZoom(16);
    };

    const formatEventTime = (dateString) => {
        return dayjs(dateString).format('MMM D, h:mm A');
    };

    const calculateETA = (durationValue) => {
        if (!durationValue) {
            return 'N/A';
        }
        const now = new Date();
        const eta = new Date(now.getTime() + durationValue * 1000);
        return dayjs(eta).format('h:mm A');
    };

    return (
        <>
            {showPanel && (
                <RoutesPanel>
                    <RoutesPanelHeader>
                        <h3>
                            <FaCalendarAlt />
                            Event Routes
                        </h3>
                        <button onClick={() => onToggle(false)}>
                            <MdClose size={20} />
                        </button>
                    </RoutesPanelHeader>

                    {loading && (
                        <LoadingOverlay>
                            <VscLoading className="spin" />
                            <p>Calculating routes...</p>
                        </LoadingOverlay>
                    )}
                    <div className="mt-1"></div>
                    <RoutesList>
                        {routes.length > 0 ? (
                            routes.map((route, index) => {
                                const isNextRoute = route.event._id === nextEvent?._id;
                                const task = route.event;
                                const now = new Date();

                                const startDate =
                                    task.eventMode === 'live' ? task?.latestDatePointer : task?.demandDate;
                                const endDate =
                                    task.eventMode === 'live' ? task?.latestDueDatePointer : task?.demandDueDate;

                                const isOngoing =
                                    startDate && endDate
                                        ? now >= new Date(startDate) && now <= new Date(endDate)
                                        : false;
                                return (
                                    <RouteItem
                                        key={route.event._id || index}
                                        // isNext={isNextRoute}
                                        onClick={() => handleEventItemClick(route.event)}
                                    >
                                        <EventMarker isNext={isNextRoute}>{index + 1}</EventMarker>
                                        <EventDetails>
                                            <h4>{route.event.title}</h4>
                                            <p>
                                                <FaClock size={12} />
                                                {formatEventTime(route.event.latestDatePointer || route.event.date)}
                                            </p>
                                            <p>
                                                <FaMapMarkerAlt size={12} />
                                                {route.event.location?.address || 'Location set'}
                                            </p>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openNavigation(route.event.location.lat, route.event.location.lng);
                                                }}
                                                className="mt-2"
                                            >
                                                <IoMdNavigate />
                                                Navigate
                                            </button>
                                        </EventDetails>
                                        <RouteInfo isNext={isNextRoute}>
                                            <div className="mt-3"></div>
                                            <span className="eta">ETA: {calculateETA(route.durationValue)}</span>
                                            <span className="distance">{route.distance}</span>
                                            <span className="distance">{route.duration}</span>
                                        </RouteInfo>
                                        {isOngoing ? (
                                            <OngoingEventBadge>ONGOING</OngoingEventBadge>
                                        ) : (
                                            <Fragment>{isNextRoute && <NextEventBadge>NEXT</NextEventBadge>}</Fragment>
                                        )}
                                    </RouteItem>
                                );
                            })
                        ) : (
                            <NoEventsMessage>
                                <FaCalendarAlt />
                                <p>No events with locations found</p>
                                <p>Add locations to your events to see routes</p>
                            </NoEventsMessage>
                        )}
                    </RoutesList>
                </RoutesPanel>
            )}

            {routes.map(
                (route, index) =>
                    route.points &&
                    route.points.length > 0 && (
                        <Polyline
                            key={`route-${route.event._id || index}`}
                            path={route.points}
                            strokeColor={route.event._id === nextEvent?._id ? '#fc5538' : '#fcae38'}
                            strokeOpacity={0.8}
                            strokeWeight={route.event._id === nextEvent?._id ? 6 : 3}
                            map={map}
                            google={google}
                        />
                    )
            )}

            {sortedEvents.map((event, index) => {
                if (!event.location || !event.location.lat || !event.location.lng) return null;

                return (
                    <Marker
                        key={event._id || `event-marker-${index}`}
                        position={{
                            lat: event.location.lat,
                            lng: event.location.lng,
                        }}
                        title={event.title}
                        onClick={(_, marker) => handleMarkerClick(event, marker)}
                        icon={{
                            url:
                                // event._id === nextEvent?._id
                                // ? 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                                'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                            scaledSize: new google.maps.Size(45, 45),
                        }}
                        map={map}
                        google={google}
                        animation={google.maps.Animation.BOUNCE}
                    />
                );
            })}
            {(selectedEvent || {})?.location && (
                <InfoWindow
                    position={{
                        lat: selectedEvent.location.lat,
                        lng: selectedEvent.location.lng,
                    }}
                    visible={true}
                    onClose={handleInfoWindowClose}
                    map={map}
                    key={`infowindow-${selectedEvent._id}-${infoWindowKey}`}
                    google={google}
                    className="bg-dark"
                >
                    <InfoWindowContent>
                        <div
                            css={css`
                                display: flex;
                                flex-direction: column;
                                justify-content: space-between;
                                align-items: center;
                                height: 130px;
                            `}
                        >
                            {(selectedEvent?.eventType || {})?.image ? (
                                <img
                                    src={(selectedEvent?.eventType || {})?.image}
                                    alt={(selectedEvent?.eventType || {})?.title}
                                    css={eventTypeIcon}
                                />
                            ) : (
                                <Avatar item={selectedEvent?.eventType || selectedEvent} />
                            )}
                            <h4 className="w-full">{selectedEvent.title}</h4>
                        </div>
                    </InfoWindowContent>
                </InfoWindow>
            )}
        </>
    );
};

const EventRoutesOverlay = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default EventRoutesOverlay;
